/**
 * useQuery hook for folder + sub-folder story counts (used to show badge
 * counts on folder nodes in the sidebar tree).
 *
 * Replaces `storyListEffects.getFoldersAndSubFoldersStoryCount` (compose-app).
 * Fires `getFolderHierarchyStoryCounts` via `postQuery` and returns the raw
 * `storyFolders` array from the response (each element carries a story count).
 *
 * Auth note (§1): non-admin reachable → `postQuery`.
 *
 * Pass `null` to disable during route transitions before `entityId > 0` (§9).
 */

import { useQuery } from '@tanstack/react-query';

import { postQuery } from '../../../effects/publications/postQuery';
import { getFolderHierarchyStoryCounts } from '../../../graphQL/queries/listQueries';
import { useToast } from '../../Toast/context/useToast.context';
import { storyListQueryKeys, FoldersAndSubFoldersStoryCountKeyParams } from './storyListQueryKeys';

export type UseFoldersAndSubFoldersStoryCountParams =
  FoldersAndSubFoldersStoryCountKeyParams | null;

export function useFoldersAndSubFoldersStoryCount(
  params: UseFoldersAndSubFoldersStoryCountParams,
  errorMessage: string
) {
  const { toggleToast } = useToast();

  return useQuery<unknown[], Error>({
    queryKey: params
      ? storyListQueryKeys.foldersAndSubFoldersStoryCount(params)
      : ([...storyListQueryKeys.all, 'foldersAndSubFoldersStoryCount', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const { entityId, folderId } = params;
      const response = await postQuery(getFolderHierarchyStoryCounts, { entityId, folderId });
      return (response?.storyFolders ?? []) as unknown[];
    },
    enabled: params !== null,
    onError: () => {
      toggleToast({ title: errorMessage, severity: 'error' });
    }
  });
}
